import { Duration } from '@packages/primitives/duration';
import { z } from 'zod';

/** How long choosing another name waits once a match has been revealed on this device. */
export const NAME_CHANGE_WAIT_MS = Duration.ofMinutes(5);

/**
 * What this browser remembers about one group. Never sent to the server.
 *
 * Confirming a name only sets `selectedParticipantId`. Revealing a match (the draw animation
 * finishing here) appends to `revealedParticipantIds`, which is what can require a wait.
 * The database `matchViewedAt` is separate: a reveal on another device does not count here.
 */
export const DeviceMemorySchema = z.object({
  selectedParticipantId: z.string().nullable(),
  /** Unique participant ids whose match was revealed on this device. The last is the latest. */
  revealedParticipantIds: z.array(z.string()),
});

export type DeviceMemory = z.infer<typeof DeviceMemorySchema>;

/** Rules never mutate memory, so this can be shared. */
export const EMPTY_DEVICE_MEMORY: DeviceMemory = {
  selectedParticipantId: null,
  revealedParticipantIds: [],
};

export const DeviceMemoryRules = {
  lastRevealedParticipantId(memory: DeviceMemory): string | null {
    return memory.revealedParticipantIds[memory.revealedParticipantIds.length - 1] ?? null;
  },

  isLastRevealed(memory: DeviceMemory, participantId: string): boolean {
    return DeviceMemoryRules.lastRevealedParticipantId(memory) === participantId;
  },

  /**
   * Milliseconds left before `participantId` can be confirmed.
   * No reveal on this device yet, or the last revealed match: no wait.
   * Any other name: 5 minutes from when its confirmation screen opened.
   */
  remainingWaitMs(
    memory: DeviceMemory,
    participantId: string,
    confirmationOpenedAt: number,
    now: number,
  ): number {
    if (
      memory.revealedParticipantIds.length === 0 ||
      DeviceMemoryRules.isLastRevealed(memory, participantId)
    ) {
      return 0;
    }
    return Math.max(0, confirmationOpenedAt + NAME_CHANGE_WAIT_MS - now);
  },

  select(memory: DeviceMemory, participantId: string): DeviceMemory {
    return { ...memory, selectedParticipantId: participantId };
  },

  /** Repeating an id moves it to the end without adding a second copy. */
  markRevealed(memory: DeviceMemory, participantId: string): DeviceMemory {
    return {
      ...memory,
      revealedParticipantIds: [
        ...memory.revealedParticipantIds.filter(id => id !== participantId),
        participantId,
      ],
    };
  },

  /** Leaving a name forgets only the selection. Reveal history is kept. */
  forget(memory: DeviceMemory): DeviceMemory {
    return { ...memory, selectedParticipantId: null };
  },
};

/** Formats a wait as m:ss, rounding up to the next whole second. */
export function formatWait(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
